import { forwardRef } from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { SideNavRailTooltip } from './SideNavRailTooltip'

type SideNavRailItemBaseProps = {
  /** The item's name. Never rendered visibly, so it becomes the accessible label. */
  label: string
  /** Icon component to display (from @/assets/icons) */
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  /** Whether the item is currently active/selected */
  isActive?: boolean
  /** Renders the icon in brand blue (New Chat) rather than the default neutral */
  tone?: 'neutral' | 'brand'
  /** Visual weight, mirroring SideNavItem. 'subtle' is the muted treatment for lightweight actions (e.g. "Back to all matters"). */
  variant?: 'default' | 'subtle'
  /** Blocks activation. A disabled `Link` also leaves the tab order, since anchors ignore `disabled`. */
  disabled?: boolean
}

export type SideNavRailItemProps = SideNavRailItemBaseProps &
  Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'onClick'> & {
    /** Navigation target. When set the item renders as a `Link`, otherwise as a `button`. */
    to?: string
    /**
     * Click handler. Optional on a button because the flyout trigger receives its handlers from
     * Radix through `asChild` rather than from the call site.
     */
    onClick?: () => void
  }

/**
 * A single icon-only target in the contracted side nav rail.
 *
 * Renders a `Link` when given `to` and a `button` when given `onClick`, so navigation items keep
 * cmd-click while action items stay real buttons.
 *
 * Carries its own tooltip. As the flyout trigger it sits under a Radix `asChild`, which composes
 * onto that tooltip and through to the element beneath it.
 *
 * For the expanded nav, use SideNavItem instead.
 */
export const SideNavRailItem = forwardRef<HTMLElement, SideNavRailItemProps>(function SideNavRailItem(
  { label, icon: Icon, isActive = false, tone = 'neutral', variant = 'default', disabled = false, to, onClick, className, ...props },
  forwardedRef
) {
  const targetClassName = clsx(
    'group flex h-9 w-9 shrink-0 items-center justify-center rounded p-2',
    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500',
    isActive ? 'bg-surface-selected-subtle' : 'hover:bg-surface-hover active:bg-surface-hover',
    disabled && 'pointer-events-none opacity-50',
    className
  )

  const iconColorClassName =
    isActive || tone === 'brand'
      ? 'text-brand-500'
      : variant === 'subtle'
      ? 'text-text-tertiary group-hover:text-brand-neutral-700'
      : 'text-text-secondary group-hover:text-brand-neutral-900'

  const icon = <Icon className={clsx('h-5 w-5 shrink-0', iconColorClassName)} aria-hidden="true" />

  const target =
    to !== undefined ? (
      <Link
        {...props}
        ref={forwardedRef as React.Ref<HTMLAnchorElement>}
        to={to}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        data-ds-component="SideNavRailItem"
        className={targetClassName}
      >
        {icon}
      </Link>
    ) : (
      <button
        {...props}
        ref={forwardedRef as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        data-ds-component="SideNavRailItem"
        className={targetClassName}
      >
        {icon}
      </button>
    )

  return <SideNavRailTooltip title={label}>{target}</SideNavRailTooltip>
})
